import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { useState } from 'react';

type PasswordFieldProps = Omit<TextFieldProps, 'type'>;

/**
 * A password box with a control to show what was typed.
 *
 * Typing a password you cannot see is guesswork, and it is worse where a new
 * password is entered twice: the only feedback used to be "the two passwords do
 * not match", after submitting, with no way to see which one was wrong.
 *
 * The control is a real button so it can be reached by keyboard, and its label
 * changes with its state so a screen reader says whether the password is
 * currently showing. It is deliberately not a checkbox: pressing it does
 * something immediately rather than setting an option.
 *
 * It deliberately sets no id of its own. The shared Field component owns the
 * input's id to tie the label and the error message to it, and an id here
 * silently broke that - the label stopped pointing at anything.
 */
export function PasswordField({ slotProps, ...props }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);

  return (
    <TextField
      {...props}
      type={visible ? 'text' : 'password'}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setVisible((shown) => !shown)}
                // Says what pressing it will do, and reflects the state, so it
                // is useful whether or not the icon can be seen.
                aria-label={visible ? 'Hide password' : 'Show password'}
                aria-pressed={visible}
                edge="end"
                size="small"
              >
                {visible ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
